/**
 * 页面级 Page_JsonLd 的纯函数层：9 个构造器 + 1 个装配器。
 *
 * 与 `organization.ts` / `website.ts` 的差别：站点级 JSON-LD 只有一套输入
 * （site-profile），本模块的每个构造器对应一个 Page_Type，输入形状各不相同，
 * 因此这里没有一张统一的 `Profile` 类型，而是逐个构造器定义最小可用的
 * `XxxLdInput`——字段全部可选，缺失时按需求 4.14/4.16 走省略/兜底路径，
 * 不强迫调用方（`useSEO()`、构建期脚本）在传参前先做一遍判空。
 *
 * 三条共享约束（不在每个构造器里各写一遍，而是集中在下面几个 helper 里）：
 * - 需求 4.13：`url` / `item` / `@id` / `target` 经 `toAbsoluteUrl()`（`sameAs` 不受此约束，
 *   但本模块的构造器都不产出 `sameAs`，与 `sameAs` 无关）
 * - 需求 4.15：`name` / `headline` / `description` 经 `clampJsonLdText()`，
 *   `name` / `headline` 归一到 4–110 字符、`description` 归一到 40–320 字符
 * - 需求 4.16：`image` 经 `toAbsoluteUrl()`，源图片字段为空时取该 Page_Type 预设的
 *   `fallbackOgImage`——由于每个构造器固定对应一个 Page_Type（`buildServiceLd` 只服务
 *   `SOLUTION_DETAIL`、`buildProductLd` 只服务 `PRODUCT_DETAIL` 等），这里直接读对应
 *   Page_Type 的预设，不需要额外的 `pageType` 参数
 *
 * 需求 4.17（`</script` 转义）不在这里处理：本模块只构造纯 JS 对象，
 * 转义交给注入点统一调用的 `serializeJsonLd()`。
 *
 * 全部为纯函数：不读 DOM、不发网络请求、不读全局状态，Node 与浏览器结果一致，
 * 供运行时 `src/utils/seo.ts`、构建期 `scripts/prerender/*` 与巡检器复用。
 *
 * 对应需求：4.1–4.17
 */

import { PAGE_TYPE_META_PRESET } from "../constants";
import type { DateInput } from "../dates";
import { clampDateModified, toIso8601Cst } from "../dates";
import type { FaqEntry } from "../faqAssembly";
import { PAGE_TYPES } from "../types";
import type { PageType } from "../types";
import { extractVisibleText } from "../visibleText";
import type { OrganizationContactPoint } from "./organization";
import { buildSiteJsonLd } from "./website";
import { clampJsonLdText, toAbsoluteUrl } from "./serialize";

/** `name` / `headline` 长度闭区间（需求 4.15） */
const HEADLINE_LENGTH = { min: 4, max: 110 } as const;

/** `description` 长度闭区间（需求 4.15） */
const DESCRIPTION_LENGTH = { min: 40, max: 320 } as const;

/** `BreadcrumbList` 中 `name` 的长度闭区间（需求 4.1，与 4.15 的 name/headline 区间不同） */
const BREADCRUMB_NAME_LENGTH = { min: 2, max: 40 } as const;

/** `Service.provider` / `CaseArticle.publisher` / `AboutPage.mainEntity` 缺省名称兜底 */
const ORG_NAME_FALLBACK = "欧格登智能科技";

/** `Service.serviceType` 缺省兜底 */
const SERVICE_TYPE_FALLBACK = "企业级解决方案";

/** `Service.areaServed` / `ContactPoint.areaServed` 缺省兜底 */
const AREA_SERVED_FALLBACK = "中国大陆";

/** `Product.brand.name` 缺省兜底：源数据没有具体品牌信息时，产品仍归属本站 */
const BRAND_NAME_FALLBACK = ORG_NAME_FALLBACK;

/** `Product.category` 缺省兜底 */
const PRODUCT_CATEGORY_FALLBACK = "企业级产品";

/** `CaseArticle.about.name` 缺省兜底 */
const CASE_ABOUT_NAME_FALLBACK = "企业信息化解决方案";

/**
 * `ContactPage.mainEntity.contactPoint` 在 `buildOrganizationLd()` 因电话字段
 * 空白而省略 `contactPoint` 时使用的兜底元素，保证「至少 1 个 `ContactPoint`」
 * 这条需求 4.10 的约束不因源数据缺电话而落空。
 */
const FALLBACK_CONTACT_POINT: readonly OrganizationContactPoint[] = [
  {
    "@type": "ContactPoint",
    telephone: "",
    contactType: "客户服务",
    areaServed: [AREA_SERVED_FALLBACK],
    availableLanguage: "zh-CN",
  },
];

/** Page_Type 判定：非法取值回落到 `HOME`（与 `metadata.ts` 同一口径） */
function isPageType(value: unknown): value is PageType {
  return typeof value === "string" && (PAGE_TYPES as readonly string[]).includes(value);
}

/** 去标签、去首尾空白后的纯文本；非字符串输入按空串处理 */
function plainText(raw: unknown): string {
  return typeof raw === "string" && raw !== "" ? extractVisibleText(raw) : "";
}

/** 判定某个来源字段是否「空或缺失」（需求 4.14 的省略触发条件） */
function isBlank(raw: unknown): boolean {
  return plainText(raw) === "";
}

/** 判定日期源值是否存在（不是「无法解析后退回兜底值」，而是真的没有源值） */
function hasDateValue(value: DateInput): boolean {
  if (value === null || value === undefined) {
    return false;
  }
  if (value instanceof Date) {
    return !Number.isNaN(value.getTime());
  }
  if (typeof value === "number") {
    return Number.isFinite(value);
  }
  return value.trim() !== "";
}

/** 非空文本兜底：去标签后非空则用源值，否则用 `fallback` */
function textOrFallback(raw: unknown, fallback: string): string {
  const text = plainText(raw);
  return text !== "" ? text : fallback;
}

/**
 * 归一化 `image`：绝对地址字符串，或至少 1 个绝对地址元素的数组（需求 3.3/4.16）。
 * 数组来源过滤空白项；单元素数组折叠为字符串，避免「明明只有 1 张图却是数组」的形态噪音。
 * 源图片整体为空时取 `fallback`（该 Page_Type 预设的 `fallbackOgImage`）。
 */
function normalizeImage(raw: unknown, fallback: string): string | string[] {
  if (Array.isArray(raw)) {
    const urls = raw
      .filter((item): item is string => typeof item === "string" && item.trim() !== "")
      .map((item) => toAbsoluteUrl(item));
    if (urls.length === 1) {
      return urls[0];
    }
    if (urls.length > 1) {
      return urls;
    }
  }
  if (typeof raw === "string" && raw.trim() !== "") {
    return toAbsoluteUrl(raw);
  }
  return toAbsoluteUrl(fallback);
}

/** 归一化 `areaServed`：非空字符串或至少 1 个非空字符串元素的数组（需求 4.2） */
function normalizeAreaServed(raw: unknown, fallback: string): string | string[] {
  if (Array.isArray(raw)) {
    const items = raw
      .filter((item): item is string => typeof item === "string" && item.trim() !== "")
      .map((item) => item.trim());
    if (items.length > 0) {
      return items;
    }
  }
  if (typeof raw === "string" && raw.trim() !== "") {
    return raw.trim();
  }
  return fallback;
}

// ---------------------------------------------------------------------------
// 1. buildBreadcrumbLd — 需求 4.1
// ---------------------------------------------------------------------------

/** `buildBreadcrumbLd()` 的单个输入条目 */
export type BreadcrumbInputItem = {
  name?: string | null;
  path?: string | null;
};

export type BreadcrumbListItemLd = {
  "@type": "ListItem";
  position: number;
  name: string;
  item: string;
};

export type BreadcrumbListLd = {
  "@context": "https://schema.org";
  "@type": "BreadcrumbList";
  itemListElement: BreadcrumbListItemLd[];
};

/**
 * 把「首页 → … → 当前页」的路径条目组装成 `BreadcrumbList` JSON-LD（需求 4.1）。
 *
 * 与 `src/utils/seo.ts` 现有 `buildBreadcrumbLd(items)` 的差别只有一处：这里多接受
 * 一个 `canonicalUrl` 参数。原因是需求 4.1 要求「末个元素的 `item` 等于该页面
 * canonical」，而 canonical 的计算（`normalizeCanonical()`）不在本模块职责内，
 * 本模块也没有其他途径知道调用方当前页的 canonical——所以由调用方算好后传入，
 * 而不是本函数反过来猜测或重新推导。`src/utils/seo.ts` 的委托改造（task 11.1）
 * 负责把旧签名适配到这个新签名，属于另一个任务的范围。
 *
 * 处理规则：
 * - 首元素强制为 `{ name: "首页", item: 站点根地址 }`，不采信 `items[0]` 的取值——
 *   需求 4.1 对首元素的措辞是不带条件的断言，构造器不应该因为调用方传错而破例
 * - 末元素的 `item` 强制为 `toAbsoluteUrl(canonicalUrl)`，不采信调用方给的 `path`
 * - 中间元素的 `item` 经 `toAbsoluteUrl(item.path)`（需求 4.13）
 * - 全部元素的 `name` 经 `clampJsonLdText(name, 2, 40)` 归一到 2–40 字符（需求 4.1），
 *   首元素固定为「首页」文本本身（已在区间内，无需再clamp）
 * - `position` 按最终数组顺序从 1 连续递增
 * - 元素数量恒落在 2–4：入参不足 2 条时用首页+当前页兜底；超过 4 条时保留首元素与
 *   最后 3 个元素（贴近叶子节点的层级对 AI_Crawler 更有信息量），使总数收敛到 4
 *
 * @param items 首页到当前页的路径条目，通常已含首页作为 `items[0]`
 * @param canonicalUrl 该页面 `<link rel="canonical">` 的取值
 */
export function buildBreadcrumbLd(
  items: readonly BreadcrumbInputItem[] | null | undefined,
  canonicalUrl: unknown,
): BreadcrumbListLd {
  const canonical = toAbsoluteUrl(canonicalUrl);
  const source = Array.isArray(items) ? items.filter((item): item is BreadcrumbInputItem => Boolean(item)) : [];

  const home: BreadcrumbInputItem = { name: "首页", path: "/" };
  const current: BreadcrumbInputItem =
    source.length > 0 ? source[source.length - 1] : { name: "", path: "" };
  const middle = source.length > 1 ? source.slice(1, source.length - 1) : [];

  let ordered = [home, ...middle, current];
  if (ordered.length > 4) {
    ordered = [ordered[0], ...ordered.slice(-3)];
  }

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: ordered.map((entry, index) => {
      const isFirst = index === 0;
      const isLast = index === ordered.length - 1;
      return {
        "@type": "ListItem",
        position: index + 1,
        name: isFirst
          ? "首页"
          : clampJsonLdText(entry?.name, BREADCRUMB_NAME_LENGTH.min, BREADCRUMB_NAME_LENGTH.max),
        item: isLast ? canonical : toAbsoluteUrl(entry?.path),
      };
    }),
  };
}

// ---------------------------------------------------------------------------
// 2. buildServiceLd — 需求 4.2，SOLUTION_DETAIL
// ---------------------------------------------------------------------------

export type ServiceLdInput = {
  /** 方案标题；与 `description` 一同缺失时省略整段（需求 4.14） */
  name?: string | null;
  /** 方案简介；与 `name` 一同缺失时省略整段（需求 4.14） */
  description?: string | null;
  serviceType?: string | null;
  /** 服务覆盖地区：非空字符串，或至少 1 个非空字符串元素的数组 */
  areaServed?: string | readonly string[] | null;
  /** 该方案详情页地址；缺失时回落站点首页（需求 4.13） */
  url?: string | null;
};

export type ServiceLd = {
  "@context": "https://schema.org";
  "@type": "Service";
  name: string;
  description: string;
  serviceType: string;
  provider: { "@type": "Organization"; name: string };
  areaServed: string | string[];
  url: string;
};

/**
 * 组装 `Service` JSON-LD（需求 4.2，仅用于 `SOLUTION_DETAIL`）。
 *
 * `name` 或 `description` 源字段为空/缺失时返回 `null`，由调用方（`assemblePageJsonLd`）
 * 省略整段并保留 Organization + WebSite + BreadcrumbList（需求 4.14）。
 *
 * @param data 方案数据，字段全部可选
 * @param orgName 站点组织名称，须与 Site_JsonLd 的 `Organization.name` 逐字符一致
 *   （需求 4.2）——因此这里只接受调用方传入的已解析结果，不在本函数内重新解析
 */
export function buildServiceLd(data: ServiceLdInput, orgName: string): ServiceLd | null {
  if (isBlank(data?.name) || isBlank(data?.description)) {
    return null;
  }

  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: clampJsonLdText(data.name, HEADLINE_LENGTH.min, HEADLINE_LENGTH.max, {
      pageType: "SOLUTION_DETAIL",
    }),
    description: clampJsonLdText(data.description, DESCRIPTION_LENGTH.min, DESCRIPTION_LENGTH.max, {
      pageType: "SOLUTION_DETAIL",
    }),
    serviceType: textOrFallback(data?.serviceType, SERVICE_TYPE_FALLBACK),
    provider: { "@type": "Organization", name: orgName },
    areaServed: normalizeAreaServed(data?.areaServed, AREA_SERVED_FALLBACK),
    url: toAbsoluteUrl(data?.url),
  };
}

// ---------------------------------------------------------------------------
// 3. buildProductLd — 需求 4.3，PRODUCT_DETAIL
// ---------------------------------------------------------------------------

export type ProductLdInput = {
  /** 产品名称；与 `description` 一同缺失时省略整段（需求 4.14） */
  name?: string | null;
  /** 产品简介；与 `name` 一同缺失时省略整段（需求 4.14） */
  description?: string | null;
  /** 产品主图；为空时按需求 4.16 回落 `PRODUCT_DETAIL` 预设的兜底图（不算缺失） */
  image?: string | readonly string[] | null;
  brandName?: string | null;
  brandType?: "Brand" | "Organization";
  category?: string | null;
  url?: string | null;
};

export type ProductLd = {
  "@context": "https://schema.org";
  "@type": "Product";
  name: string;
  description: string;
  image: string | string[];
  brand: { "@type": "Brand" | "Organization"; name: string };
  category: string;
  url: string;
};

/**
 * 组装 `Product` JSON-LD（需求 4.3，仅用于 `PRODUCT_DETAIL`）。
 *
 * `name` 或 `description` 源字段为空/缺失时返回 `null`（需求 4.14）；
 * `image` 为空时回落 `PAGE_TYPE_META_PRESET.PRODUCT_DETAIL.fallbackOgImage`，
 * 这一情形不算「缺失」，仍正常输出整段（需求 4.14 括号内的明确说明）。
 */
export function buildProductLd(data: ProductLdInput): ProductLd | null {
  if (isBlank(data?.name) || isBlank(data?.description)) {
    return null;
  }

  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: clampJsonLdText(data.name, HEADLINE_LENGTH.min, HEADLINE_LENGTH.max, {
      pageType: "PRODUCT_DETAIL",
    }),
    description: clampJsonLdText(data.description, DESCRIPTION_LENGTH.min, DESCRIPTION_LENGTH.max, {
      pageType: "PRODUCT_DETAIL",
    }),
    image: normalizeImage(data?.image, PAGE_TYPE_META_PRESET.PRODUCT_DETAIL.fallbackOgImage),
    brand: {
      "@type": data?.brandType === "Organization" ? "Organization" : "Brand",
      name: textOrFallback(data?.brandName, BRAND_NAME_FALLBACK),
    },
    category: textOrFallback(data?.category, PRODUCT_CATEGORY_FALLBACK),
    url: toAbsoluteUrl(data?.url),
  };
}

// ---------------------------------------------------------------------------
// 4. buildArticleLd — 需求 4.4、4.5，NEWS_DETAIL
// ---------------------------------------------------------------------------

export type ArticleLdInput = {
  /** 资讯标题；与 `datePublished` 一同缺失时省略整段（需求 4.14） */
  headline?: string | null;
  description?: string | null;
  /** 资讯封面图；为空时回落 `NEWS_DETAIL` 预设兜底图（不算缺失） */
  image?: string | readonly string[] | null;
  /** 取自 `biz_news.publish_date`；与 `headline` 一同缺失时省略整段（需求 4.14） */
  datePublished?: DateInput;
  /** 取自该记录 `update_time`（需求 4.5） */
  dateModified?: DateInput;
  authorName?: string | null;
  authorType?: "Organization" | "Person";
  url?: string | null;
};

export type ArticlePartyLd = { "@type": "Organization" | "Person"; name: string };

export type ArticleLd = {
  "@context": "https://schema.org";
  "@type": "Article";
  headline: string;
  description: string;
  image: string | string[];
  datePublished: string;
  dateModified: string;
  author: ArticlePartyLd;
  publisher: ArticlePartyLd;
  mainEntityOfPage: { "@type": "WebPage"; "@id": string };
  url: string;
};

/**
 * 组装 `Article` JSON-LD（需求 4.4、4.5，仅用于 `NEWS_DETAIL`）。
 *
 * `headline` 或 `datePublished` 源字段缺失时返回 `null`（需求 4.14——Article 的省略
 * 触发条件是「标题或发布时间」，与 Service/Product 的「标题或简介」不同，逐条对应
 * 需求 4.14 原文）。`datePublished` / `dateModified` 经 `toIso8601Cst()` /
 * `clampDateModified()` 归一（需求 4.5），`mainEntityOfPage.@id` 与 `url` 取同一个
 * 归一后的绝对地址，天然满足「二者相等」（需求 4.4）。
 *
 * @param data 资讯数据，字段全部可选
 * @param orgName `author` / `publisher` 缺失具体人名时的兜底组织名
 */
export function buildArticleLd(data: ArticleLdInput, orgName: string): ArticleLd | null {
  if (isBlank(data?.headline) || !hasDateValue(data?.datePublished)) {
    return null;
  }

  const url = toAbsoluteUrl(data?.url);
  const datePublished = toIso8601Cst(data.datePublished);
  const dateModified = clampDateModified(data.datePublished, data?.dateModified);

  const author: ArticlePartyLd = {
    "@type": data?.authorType === "Person" ? "Person" : "Organization",
    name: textOrFallback(data?.authorName, orgName),
  };

  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: clampJsonLdText(data.headline, HEADLINE_LENGTH.min, HEADLINE_LENGTH.max, {
      pageType: "NEWS_DETAIL",
    }),
    description: clampJsonLdText(data?.description, DESCRIPTION_LENGTH.min, DESCRIPTION_LENGTH.max, {
      pageType: "NEWS_DETAIL",
    }),
    image: normalizeImage(data?.image, PAGE_TYPE_META_PRESET.NEWS_DETAIL.fallbackOgImage),
    datePublished,
    dateModified,
    author,
    publisher: { "@type": "Organization", name: orgName },
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    url,
  };
}

// ---------------------------------------------------------------------------
// 5. buildCaseArticleLd — 需求 4.6，CASE_DETAIL
// ---------------------------------------------------------------------------

export type CaseArticleLdInput = {
  /** 案例标题；与 `description` 一同缺失时省略整段（需求 4.14） */
  headline?: string | null;
  /** 案例简介；与 `headline` 一同缺失时省略整段（需求 4.14） */
  description?: string | null;
  image?: string | readonly string[] | null;
  aboutName?: string | null;
  aboutType?: "Service" | "Organization";
  url?: string | null;
};

export type CaseArticleAboutLd = { "@type": "Service" | "Organization"; name: string };

export type CaseArticleLd = {
  "@context": "https://schema.org";
  "@type": "Article";
  headline: string;
  description: string;
  image: string | string[];
  about: CaseArticleAboutLd;
  publisher: { "@type": "Organization"; name: string };
  url: string;
};

/**
 * 组装案例详情的 `Article` JSON-LD（需求 4.6，仅用于 `CASE_DETAIL`）。
 *
 * `headline` 或 `description` 源字段缺失时返回 `null`。`publisher.name` 须与
 * Site_JsonLd 的 `Organization.name` 逐字符一致（需求 4.6），因此同 `buildServiceLd`，
 * 只接受调用方传入的已解析组织名，不在本函数内重新解析。
 */
export function buildCaseArticleLd(data: CaseArticleLdInput, orgName: string): CaseArticleLd | null {
  if (isBlank(data?.headline) || isBlank(data?.description)) {
    return null;
  }

  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: clampJsonLdText(data.headline, HEADLINE_LENGTH.min, HEADLINE_LENGTH.max, {
      pageType: "CASE_DETAIL",
    }),
    description: clampJsonLdText(data.description, DESCRIPTION_LENGTH.min, DESCRIPTION_LENGTH.max, {
      pageType: "CASE_DETAIL",
    }),
    image: normalizeImage(data?.image, PAGE_TYPE_META_PRESET.CASE_DETAIL.fallbackOgImage),
    about: {
      "@type": data?.aboutType === "Organization" ? "Organization" : "Service",
      name: textOrFallback(data?.aboutName, CASE_ABOUT_NAME_FALLBACK),
    },
    publisher: { "@type": "Organization", name: orgName },
    url: toAbsoluteUrl(data?.url),
  };
}

// ---------------------------------------------------------------------------
// 6. buildCollectionPageLd — 需求 4.7、4.8
// ---------------------------------------------------------------------------

/**
 * 设计选择：`SOLUTION_CATEGORY`（需求 4.7）与 4 个列表页（需求 4.8）的 `CollectionPage`
 * 是同一个 schema.org 形状——`name` / `description` / `url` / `mainEntity`，
 * `mainEntity` 都是 `ItemList`——两条验收标准的差别只在于「`itemListElement` 数量该
 * 反映什么统计口径」（分类下已发布方案数 vs. 列表页第一页展示条数），而这是调用方
 * 组装 `items` 时就该决定好的事，不是 JSON-LD 形状层面的差异。因此这里只导出
 * **一个**函数而不是两个，用 `items` 数组本身承载「已经算好的条目集合」，
 * 构造器不再区分「分类」还是「列表」这两种调用场景。
 */
export type CollectionPageItemInput = {
  name?: string | null;
  url?: string | null;
};

export type CollectionPageLdInput = {
  name?: string | null;
  description?: string | null;
  url?: string | null;
  /**
   * 已发布内容列表。`null` / `undefined` 表示获取失败（需求 4.14 的「列表数据缺失」），
   * 触发整段省略；空数组 `[]` 表示「该分类/该列表确实没有已发布内容」——这是合法数据，
   * 不算缺失，因此仍正常输出（`itemListElement` 为空数组、`numberOfItems` 为 0）。
   */
  items?: readonly CollectionPageItemInput[] | null;
  /** 决定 `name` / `description` 兜底文案的取用来源；缺省时用通用兜底语料 */
  pageType?: PageType;
};

export type CollectionPageListItemLd = {
  "@type": "ListItem";
  position: number;
  name: string;
  url: string;
};

export type CollectionPageLd = {
  "@context": "https://schema.org";
  "@type": "CollectionPage";
  name: string;
  description: string;
  url: string;
  mainEntity: {
    "@type": "ItemList";
    numberOfItems: number;
    itemListElement: CollectionPageListItemLd[];
  };
};

/** `CollectionPage.mainEntity.itemListElement` 的元素数量上限（需求 4.7、4.8） */
const COLLECTION_ITEM_LIMIT = 50;

/**
 * 组装 `CollectionPage` JSON-LD（需求 4.7、4.8）。
 *
 * `items` 为 `null` / `undefined`（获取失败）时返回 `null`；为空数组时正常输出
 * （见 `CollectionPageLdInput.items` 的文档，这是需求 4.14「列表数据缺失」括号内
 * 特别澄清的边界）。`itemListElement` 按传入顺序取前 50 项，`numberOfItems` 恒等于
 * 该数组长度（需求 4.7）。
 */
export function buildCollectionPageLd(data: CollectionPageLdInput): CollectionPageLd | null {
  if (!Array.isArray(data?.items)) {
    return null;
  }

  const options = data.pageType ? { pageType: data.pageType } : undefined;
  const itemListElement: CollectionPageListItemLd[] = data.items
    .slice(0, COLLECTION_ITEM_LIMIT)
    .map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: textOrFallback(item?.name, `内容 ${index + 1}`),
      url: toAbsoluteUrl(item?.url),
    }));

  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: clampJsonLdText(data.name, HEADLINE_LENGTH.min, HEADLINE_LENGTH.max, options),
    description: clampJsonLdText(data.description, DESCRIPTION_LENGTH.min, DESCRIPTION_LENGTH.max, options),
    url: toAbsoluteUrl(data.url),
    mainEntity: {
      "@type": "ItemList",
      numberOfItems: itemListElement.length,
      itemListElement,
    },
  };
}

// ---------------------------------------------------------------------------
// 7. buildAboutPageLd — 需求 4.9，/about
// ---------------------------------------------------------------------------

export type AboutPageLdInput = {
  name?: string | null;
  description?: string | null;
  url?: string | null;
};

export type AboutPageLd = {
  "@context": "https://schema.org";
  "@type": "AboutPage";
  name: string;
  description: string;
  url: string;
  mainEntity: { "@type": "Organization"; name: string };
};

/**
 * 组装 `AboutPage` JSON-LD（需求 4.9，仅用于 `/about`）。
 *
 * `/about` 是站点固定存在的静态页面，不像方案/产品/资讯详情那样会出现「内容记录
 * 不存在」的情形，因此本函数恒返回对象、没有 `null` 分支——`name` / `description`
 * 缺失时按需求 4.15/`ABOUT` 预设走兜底补足，而不是省略整段。
 *
 * @param orgName `mainEntity.name`，须与 Site_JsonLd 的 `Organization.name` 一致（需求 4.9）
 */
export function buildAboutPageLd(data: AboutPageLdInput, orgName: string): AboutPageLd {
  return {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: clampJsonLdText(data?.name, HEADLINE_LENGTH.min, HEADLINE_LENGTH.max, { pageType: "ABOUT" }),
    description: clampJsonLdText(data?.description, DESCRIPTION_LENGTH.min, DESCRIPTION_LENGTH.max, {
      pageType: "ABOUT",
    }),
    url: toAbsoluteUrl(data?.url),
    mainEntity: { "@type": "Organization", name: orgName },
  };
}

// ---------------------------------------------------------------------------
// 8. buildContactPageLd — 需求 4.10，/contact
// ---------------------------------------------------------------------------

export type ContactPageLdInput = {
  name?: string | null;
  url?: string | null;
};

export type ContactPageLd = {
  "@context": "https://schema.org";
  "@type": "ContactPage";
  name: string;
  url: string;
  mainEntity: {
    "@type": "Organization";
    name: string;
    contactPoint: OrganizationContactPoint[];
  };
};

/**
 * 组装 `ContactPage` JSON-LD（需求 4.10，仅用于 `/contact`）。
 *
 * `contactPoint` 直接复用 `buildOrganizationLd()` 的结果，不重新实现——调用方
 * （通常是 `assemblePageJsonLd`）应把 `buildOrganizationLd(profile).jsonLd.contactPoint`
 * 原样传入。`buildOrganizationLd()` 在电话字段空白时会省略 `contactPoint`
 * （需求 3.10），但需求 4.10 要求 `/contact` 的 `contactPoint` 数组「至少含 1 个元素」
 * 是硬性约束，不能因为源数据缺电话就跌破——因此该数组为空/缺失时改用
 * `FALLBACK_CONTACT_POINT`，这是对复用结果的兜底补充，不是重新实现其构造逻辑。
 *
 * @param orgName `mainEntity.name`
 * @param contactPoint `buildOrganizationLd(profile).jsonLd.contactPoint`
 */
export function buildContactPageLd(
  data: ContactPageLdInput,
  orgName: string,
  contactPoint?: readonly OrganizationContactPoint[] | null,
): ContactPageLd {
  return {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    name: clampJsonLdText(data?.name, HEADLINE_LENGTH.min, HEADLINE_LENGTH.max, { pageType: "CONTACT" }),
    url: toAbsoluteUrl(data?.url),
    mainEntity: {
      "@type": "Organization",
      name: orgName,
      contactPoint:
        Array.isArray(contactPoint) && contactPoint.length > 0
          ? [...contactPoint]
          : [...FALLBACK_CONTACT_POINT],
    },
  };
}

// ---------------------------------------------------------------------------
// 9. buildFaqPageLd — 需求 6.3、6.4
// ---------------------------------------------------------------------------

export type FaqPageQuestionLd = {
  "@type": "Question";
  name: string;
  acceptedAnswer: { "@type": "Answer"; text: string };
};

export type FaqPageLd = {
  "@context": "https://schema.org";
  "@type": "FAQPage";
  mainEntity: FaqPageQuestionLd[];
};

/**
 * 组装 `FAQPage` JSON-LD（需求 6.3、6.4）。
 *
 * `mainEntity` 元素顺序与传入的 `entries` 顺序一致（即 FAQ_Block 的出现顺序）；
 * `name` / `text` 只做去首尾空白，不经过 `clampJsonLdText()` 的截断/补足流水线——
 * 需求 6.4 要求这两个值必须与 FAQ_Block 展示文本「去首尾空白后逐字符一致」，
 * 任何额外的裁剪或补足都会打破这条一致性。
 *
 * `entries` 为空/缺失时返回 `null`：FAQ_Block 与 `FAQPage` JSON-LD 共用
 * `shouldRenderFaqBlock()` 的判定（需求 6.9、6.12），调用方应只在决定渲染
 * FAQ_Block 时才传入非空 `entries`；这里的空值兜底只是防御性的第二道保险。
 */
export function buildFaqPageLd(entries: readonly FaqEntry[] | null | undefined): FaqPageLd | null {
  if (!Array.isArray(entries) || entries.length === 0) {
    return null;
  }

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: entries.map((entry) => ({
      "@type": "Question",
      name: typeof entry?.question === "string" ? entry.question.trim() : "",
      acceptedAnswer: {
        "@type": "Answer",
        text: typeof entry?.answer === "string" ? entry.answer.trim() : "",
      },
    })),
  };
}

// ---------------------------------------------------------------------------
// 10. assemblePageJsonLd — 需求 4.12、4.14
// ---------------------------------------------------------------------------

/** `assemblePageJsonLd()` 的完整输入：站点级 + 面包屑 + FAQ + 各 Page_Type 专属数据 */
export type AssemblePageJsonLdData = {
  /** `GET /api/front/geo/site-profile` 响应，供 Organization/WebSite 与各处 `orgName` 使用 */
  siteProfile?: Parameters<typeof buildSiteJsonLd>[0];
  /** 该页面 `<link rel="canonical">` 的取值 */
  canonicalUrl?: string | null;
  /** 面包屑路径条目（不含末项强制覆盖逻辑，由 `buildBreadcrumbLd()` 处理） */
  breadcrumbItems?: readonly BreadcrumbInputItem[] | null;
  /** 该页面 FAQ_Block 的候选条目（通常是 `assembleFaqBlock()` 的结果） */
  faqEntries?: readonly FaqEntry[] | null;
  service?: ServiceLdInput | null;
  product?: ProductLdInput | null;
  article?: ArticleLdInput | null;
  caseArticle?: CaseArticleLdInput | null;
  collectionPage?: CollectionPageLdInput | null;
  aboutPage?: AboutPageLdInput | null;
  contactPage?: ContactPageLdInput | null;
};

/** `assemblePageJsonLd()` 的返回值 */
export type AssemblePageJsonLdResult = {
  /** 该页面全部 JSON-LD 段，按 Organization → WebSite → BreadcrumbList → … 的顺序排列 */
  blocks: unknown[];
  /** 因来源数据缺失被省略的段/字段名，供调用方记日志（需求 4.14） */
  omitted: string[];
};

/** Page_Type 到其专属数据字段与 `@type` 名称的映射，装配逻辑按表驱动而不是写分支 */
const COLLECTION_PAGE_TYPES: ReadonlySet<PageType> = new Set([
  "SOLUTION_LIST",
  "SOLUTION_CATEGORY",
  "PRODUCT_LIST",
  "CASE_LIST",
  "NEWS_LIST",
]);

/**
 * 汇总某个页面的全部 Page_JsonLd 段（需求 4.12、4.14）。
 *
 * 恒同时包含 Organization、WebSite 两段（复用 `buildSiteJsonLd()`）；`pageType` 不为
 * `HOME` 时额外恒包含 BreadcrumbList 一段——这三段不受任何来源数据缺失影响，
 * 缺失的只可能是「页面专属段」与「FAQPage 段」。
 *
 * 页面专属段按 `pageType` 选取对应构造器：`SOLUTION_DETAIL` → `Service`、
 * `PRODUCT_DETAIL` → `Product`、`NEWS_DETAIL` / `CASE_DETAIL` → `Article`、
 * 5 个列表/分类页 → `CollectionPage`、`ABOUT` / `CONTACT` → 各自的静态页面类型；
 * 构造器返回 `null`（来源数据缺失）时该段省略、段名记入 `omitted`，其余段不受影响。
 *
 * `FAQPage` 段仅在 `PAGE_TYPE_META_PRESET[pageType].renderFaq` 为真且 `faqEntries`
 * 非空时输出（需求 6.1、6.12），与 `buildFaqPageLd()` 的空值兜底共同保证「候选条目
 * 不足时不输出」不会意外产出一段空 `FAQPage`。
 *
 * 由于每个 `pageType` 分支至多产出 1 个页面专属段，加上恒定的 Organization/WebSite/
 * BreadcrumbList 与可选的 FAQPage，任意 `@type` 在结果中最多出现 1 段（需求 4.12）；
 * `HOME` 无专属段与无面包屑，段数范围为 2（无 FAQ）–3（有 FAQ），落在 2–4 之内；
 * 其余 Page_Type 段数范围为 3（无专属段无 FAQ）–5（专属段+FAQ 都有），落在 3–8 之内。
 */
export function assemblePageJsonLd(
  pageType: PageType,
  data: AssemblePageJsonLdData = {},
): AssemblePageJsonLdResult {
  const resolvedPageType = isPageType(pageType) ? pageType : "HOME";
  const omitted: string[] = [];

  const { organization, website } = buildSiteJsonLd(data.siteProfile);
  const orgName = organization.jsonLd.name;
  const canonical = toAbsoluteUrl(data.canonicalUrl);

  const blocks: unknown[] = [organization.jsonLd, website];

  if (resolvedPageType !== "HOME") {
    blocks.push(buildBreadcrumbLd(data.breadcrumbItems, canonical));
  }

  if (resolvedPageType === "SOLUTION_DETAIL") {
    const service = buildServiceLd({ ...data.service, url: data.service?.url ?? canonical }, orgName);
    if (service) {
      blocks.push(service);
    } else {
      omitted.push("Service");
    }
  } else if (resolvedPageType === "PRODUCT_DETAIL") {
    const product = buildProductLd({ ...data.product, url: data.product?.url ?? canonical });
    if (product) {
      blocks.push(product);
    } else {
      omitted.push("Product");
    }
  } else if (resolvedPageType === "NEWS_DETAIL") {
    const article = buildArticleLd({ ...data.article, url: data.article?.url ?? canonical }, orgName);
    if (article) {
      blocks.push(article);
    } else {
      omitted.push("Article");
    }
  } else if (resolvedPageType === "CASE_DETAIL") {
    const caseArticle = buildCaseArticleLd(
      { ...data.caseArticle, url: data.caseArticle?.url ?? canonical },
      orgName,
    );
    if (caseArticle) {
      blocks.push(caseArticle);
    } else {
      omitted.push("Article");
    }
  } else if (COLLECTION_PAGE_TYPES.has(resolvedPageType)) {
    const collection = buildCollectionPageLd({
      ...data.collectionPage,
      url: data.collectionPage?.url ?? canonical,
      pageType: data.collectionPage?.pageType ?? resolvedPageType,
    });
    if (collection) {
      blocks.push(collection);
    } else {
      omitted.push("CollectionPage");
    }
  } else if (resolvedPageType === "ABOUT") {
    blocks.push(
      buildAboutPageLd({ ...data.aboutPage, url: data.aboutPage?.url ?? canonical }, orgName),
    );
  } else if (resolvedPageType === "CONTACT") {
    blocks.push(
      buildContactPageLd(
        { ...data.contactPage, url: data.contactPage?.url ?? canonical },
        orgName,
        organization.jsonLd.contactPoint,
      ),
    );
  }

  const preset = PAGE_TYPE_META_PRESET[resolvedPageType];
  if (preset.renderFaq) {
    const faq = buildFaqPageLd(data.faqEntries);
    if (faq) {
      blocks.push(faq);
    } else {
      omitted.push("FAQPage");
    }
  }

  return { blocks, omitted };
}
